import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useActivityService } from '../services/activityService';

const useUsers = () => {
  const { user: currentUser } = useAuth();
  const { addActivity, TYPES } = useActivityService();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load users from localStorage if available
  useEffect(() => {
    const storedUsers = localStorage.getItem('users');
    if (storedUsers) {
      setUsers(JSON.parse(storedUsers));
    } else {
      // Save initial users to localStorage only if not already set
      const initialUsers = [
        {
          id: '1',
          firstName: 'Khouloud',
          lastName: 'Bhlel',
          email: 'khouloud.bhlel@gmail.com',
          role_id: 1,
          isActive: false,
          profileImage: '',
          phoneNumber: '+216 93 023 777',
          department: 'Administration',
          joinDate: '2025-08-20',
          lastLogin: null,
          lastLogout: null
        },
        {
          id: '2',
          firstName: 'Raghda',
          lastName: 'Mhiri',
          email: 'raghdamhiri@gmail.com',
          role_id: 2, // Admin role
          isActive: false,
          profileImage: '',
          phoneNumber: '+216 28 875 952',
          department: 'Content Management',
          joinDate: '2025-08-20',
          lastLogin: null,
          lastLogout: null
        }
      ];
      localStorage.setItem('users', JSON.stringify(initialUsers));
      setUsers(initialUsers);
    }
  }, []);

  // Function to reset user data (useful for development)
  const resetUserData = () => {
    localStorage.removeItem('users');
    const initialUsers = [
      {
        id: '1',
        firstName: 'Khouloud',
        lastName: 'Bhlel',
        email: 'khouloud.bhlel@gmail.com',
        role_id: 1,
        isActive: false,
        profileImage: '',
        phoneNumber: '+216 93 023 777',
        department: 'Administration',
        joinDate: '2025-08-20',
        lastLogin: null,
        lastLogout: null
      },
      {
        id: '2',
        firstName: 'Raghda',
        lastName: 'Mhiri',
        email: 'raghdamhiri@gmail.com',
        role_id: 2,
        isActive: false,
        profileImage: '',
        phoneNumber: '+216 28 875 952',
        department: 'Content Management',
        joinDate: '2025-08-20',
        lastLogin: null,
        lastLogout: null
      }
    ];
    localStorage.setItem('users', JSON.stringify(initialUsers));
    setUsers(initialUsers);
  };

  // Update current user status based on auth context
  useEffect(() => {
    if (currentUser) {
      setUsers(prev => {
        const updatedUsers = prev.map(u => 
          u.email === currentUser.email 
            ? { 
                ...u, 
                isActive: true, 
                lastLogin: currentUser.lastLogin || new Date().toISOString() 
              }
            : { ...u, isActive: false } // Set other users as inactive
        );
        localStorage.setItem('users', JSON.stringify(updatedUsers));
        return updatedUsers;
      });
    } else {
      // If no user is logged in, mark all users as inactive
      setUsers(prev => {
        const updatedUsers = prev.map(u => ({ ...u, isActive: false }));
        localStorage.setItem('users', JSON.stringify(updatedUsers));
        return updatedUsers;
      });
    }
  }, [currentUser]);

  // Load users from localStorage
  const loadUsers = () => {
    const storedUsers = localStorage.getItem('users');
    if (storedUsers) {
      setUsers(JSON.parse(storedUsers));
    }
  };

  const createUser = async (userData) => {
    setLoading(true);
    try {
      const newUser = {
        ...userData,
        id: userData.id || Date.now().toString(),
        isActive: false, // New users are inactive by default
        lastLogin: null,
        lastLogout: null
      };
      
      const updatedUsers = [...users, newUser];
      setUsers(updatedUsers);
      localStorage.setItem('users', JSON.stringify(updatedUsers));
      
      // Log user creation activity
      await addActivity(TYPES.USER_ADD, `New user created: ${newUser.firstName} ${newUser.lastName} (${newUser.email})`);
      
      return newUser;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, userData) => {
    setLoading(true);
    try {
      const existingUser = users.find(u => u.id === userId);
      const updatedUsers = users.map(u => u.id === userId ? { ...u, ...userData } : u);
      setUsers(updatedUsers);
      localStorage.setItem('users', JSON.stringify(updatedUsers));
      
      // Log user update activity
      if (existingUser) {
        await addActivity(TYPES.USER_EDIT, `User updated: ${existingUser.firstName} ${existingUser.lastName} profile modified`);
      }
      
      return userData;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    setLoading(true);
    try {
      const userToDelete = users.find(u => u.id === userId);
      const updatedUsers = users.filter(u => u.id !== userId);
      setUsers(updatedUsers);
      localStorage.setItem('users', JSON.stringify(updatedUsers));
      
      // Log user deletion activity
      if (userToDelete) {
        await addActivity(TYPES.USER_DELETE, `User deleted: ${userToDelete.firstName} ${userToDelete.lastName} (${userToDelete.email})`);
      }
      
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get user online status
  const getOnlineStatus = (user) => {
    if (!user.isActive) return 'Offline';
    if (user.email === currentUser?.email) return 'Online';
    return 'Offline';
  };

  // Get user activity status color
  const getActivityStatusColor = (user) => {
    if (user.isActive && user.email === currentUser?.email) return 'online';
    return 'offline';
  };

  const getRoleName = (roleId) => {
    switch(roleId) {
      case 1: return 'Super Admin';
      case 2: return 'Admin';
      case 3: return 'User';
      default: return 'User';
    }
  };

  const getRoleColor = (roleId) => {
    switch(roleId) {
      case 1: return 'super-admin';
      case 2: return 'admin';
      case 3: return 'user';
      default: return 'user';
    }
  };

  return {
    users,
    loading,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    getRoleName,
    getRoleColor,
    getOnlineStatus,
    getActivityStatusColor,
    resetUserData
  };
};

export default useUsers;
